import {
  getMonthReferralStatsQuery,
  getReferralEarningsSummaryQuery,
  GetReferralProfile,
  getReferralProfileQuery,
  GetReferralProfileQueryKey
} from '@/api/referrals/get-referrals';
import { useGraphqlClient } from '@/libs/amplify/client';
import { Schema } from '@/schema';
import { useAuthStore } from '@/stores/auth-store-provider';
import { throwGraphqlError } from '@/utils/throw-graphql-error';
import { GridPaginationModel } from '@mui/x-data-grid/models/gridPaginationProps';
import type { DefaultError } from '@tanstack/react-query';
import {
  keepPreviousData,
  useQuery,
  UseQueryOptions
} from '@tanstack/react-query';

export function useGetReferralProfile(
  options: Omit<
    UseQueryOptions<
      GetReferralProfile | null,
      DefaultError,
      GetReferralProfile | null,
      GetReferralProfileQueryKey
    >,
    'queryFn' | 'queryKey'
  > = {}
) {
  const authStore = useAuthStore();
  const owner = authStore.useTracked.userId() ?? '';
  const graphqlClient = useGraphqlClient();
  return useQuery({
    ...getReferralProfileQuery(graphqlClient, owner),
    ...options
  });
}

export function useGetMonthReferralStats(month?: string) {
  const authStore = useAuthStore();
  const owner = authStore.useTracked.userId() ?? '';
  const graphqlClient = useGraphqlClient();
  return useQuery(getMonthReferralStatsQuery(graphqlClient, owner, month));
}

export function useGetReferralEarningsSummary() {
  const authStore = useAuthStore();
  const owner = authStore.useTracked.userId() ?? '';
  const graphqlClient = useGraphqlClient();
  return useQuery(getReferralEarningsSummaryQuery(graphqlClient, owner));
}

export function listReferralLeadsQueryKey(
  owner: string,
  paginationModel: GridPaginationModel
) {
  return [owner, 'ListReferralLeads', paginationModel] as const;
}

export function listReferralRewardsQueryKey(
  owner: string,
  paginationModel: GridPaginationModel
) {
  return [owner, 'ListReferralRewards', paginationModel] as const;
}

export type ReferralLead = Schema['ReferralLead']['type'];
export type ReferralReward = Schema['ReferralReward']['type'];

export function useListReferralLeads(paginationModel: GridPaginationModel) {
  const authStore = useAuthStore();
  const owner = authStore.useTracked.userId() ?? '';
  const queryKey = listReferralLeadsQueryKey(owner, paginationModel);
  const graphqlClient = useGraphqlClient();

  return useQuery({
    queryKey,
    queryFn: async () => {
      const { data, errors } = await graphqlClient.queries.listReferralLeads({
        pageSize: paginationModel.pageSize,
        page: paginationModel.page + 1
      });
      throwGraphqlError(errors);

      return data ?? null;
    },
    placeholderData: keepPreviousData
  });
}

export function useListReferralRewards(paginationModel: GridPaginationModel) {
  const authStore = useAuthStore();
  const owner = authStore.useTracked.userId() ?? '';
  const queryKey = listReferralRewardsQueryKey(owner, paginationModel);
  const graphqlClient = useGraphqlClient();

  return useQuery({
    queryKey,
    queryFn: async () => {
      const { data, errors } = await graphqlClient.queries.listReferralRewards({
        pageSize: paginationModel.pageSize,
        page: paginationModel.page + 1
      });
      throwGraphqlError(errors);

      return data ?? null;
    },
    placeholderData: keepPreviousData
  });
}
